// src/app/(public)/page.tsx
// Homepage component - Trying plain Link for Hero CTA
'use client'; // Required for hooks and event handlers

import React, { useRef, useState } from 'react'; // Added useState
import { Button } from '@/components/ui/button';
import {
  CheckCircle,
  Calculator,
  Book,
  ChevronDown,
  DollarSign,
  Briefcase,
  AlertCircle,
  Loader2,
  Check,
} from 'lucide-react';
import ServiceCard from '@/components/layout/ServiceCard';
import TestimonialCard from '@/components/layout/TestimonialCard';
import HeroSection from '@/components/layout/HeroSection';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './components/ui/form';
import { Input } from './components/ui/input';
import { CustomCalendar } from './components/ui/custom-calendar';
import { Alert, AlertDescription, AlertTitle } from './components/ui/alert';

import { useForm, SubmitHandler, SubmitErrorHandler } from 'react-hook-form'; // Import react-hook-form
import { zodResolver } from '@hookform/resolvers/zod'; // Import resolver
import * as z from 'zod'; // Import zod for schema validation
import { Textarea } from './components/ui/textarea';
import { API_BASE_URL } from './lib/api';
import { cn } from '@/lib/utils';

/**
 * Format a calendar-picked date as YYYY-MM-DD in the visitor's local time.
 * (toISOString() converts to UTC first, which shifts the day for visitors
 * east of UTC — they would book the day before the one they picked.)
 */
function formatLocalDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Steps of the Consultation Request accordion form
type FormStep = 1 | 2 | 3;

// Convert a 24-hour "HH:00" slot to a 12-hour display string like "1:00 PM"
function formatTimeSlot(timeSlot: string): string {
  const hour = parseInt(timeSlot.split(':')[0]);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:00 ${ampm}`;
}

interface AccordionSectionProps {
  step: FormStep;
  title: string;
  summary?: string;
  isOpen: boolean;
  isComplete: boolean;
  isReachable: boolean;
  onHeaderClick: () => void;
  children: React.ReactNode;
}

/**
 * Collapsible step section for the Consultation Request form.
 * Content stays mounted while collapsed so field values are preserved.
 */
function AccordionSection({
  step,
  title,
  summary,
  isOpen,
  isComplete,
  isReachable,
  onHeaderClick,
  children,
}: AccordionSectionProps) {
  return (
    <div
      className={cn(
        'rounded-lg border transition-colors duration-300',
        isOpen
          ? 'border-tifpa-sage-600/50 bg-white shadow-sm'
          : 'border-stone-200 bg-white/60'
      )}
    >
      <button
        type="button"
        onClick={onHeaderClick}
        disabled={!isReachable}
        className="w-full flex items-center gap-3 p-4 text-left disabled:cursor-not-allowed"
        aria-expanded={isOpen}
      >
        <span
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-medium transition-colors',
            isComplete
              ? 'bg-tifpa-sage-600 text-white'
              : isOpen
                ? 'bg-stone-800 text-white'
                : 'bg-stone-200 text-stone-500'
          )}
        >
          {isComplete ? <Check className="h-4 w-4" /> : step}
        </span>
        <span className="flex-1 min-w-0">
          <span
            className={cn(
              'block font-medium',
              isReachable ? 'text-stone-800' : 'text-stone-400'
            )}
          >
            {title}
          </span>
          {!isOpen && summary && (
            <span className="block text-sm text-muted-foreground truncate">
              {summary}
            </span>
          )}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-stone-400 transition-transform duration-300',
            isOpen && 'rotate-180'
          )}
        />
      </button>
      <div
        className={cn(
          'grid transition-all duration-300 ease-in-out',
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="overflow-hidden">
          <div className="p-4 pt-1 space-y-4">{children}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Homepage component.
 * Defines the structure and content for the main landing page.
 * Uses shadcn UI components and placeholder content.
 */
export default function App() {
  const testimonials = [
    {
      name: 'Michael Nguyen',
      business: 'MN Contracting',
      testimonial:
        'TIFPA Consulting transformed our financial management. Their expertise in contractor accounting has saved us thousands in taxes and helped us grow our business.',
      rating: 5,
    },
    {
      name: 'Linda Tran',
      business: 'Lotus Beauty Salon',
      testimonial:
        "As a small business owner, I needed someone who understood both my financial and cultural context. They've been an invaluable partner for over 5 years.",
      rating: 5,
    },
    {
      name: 'David Pham',
      business: 'DP Construction',
      testimonial:
        'Their bilingual services made tax season stress-free for me. They explain complex tax matters in ways I can understand, in either English or Vietnamese.',
      rating: 4,
    },
  ];

  const services = [
    {
      title: 'Tax Preparation',
      description:
        'Expert tax preparation for individuals, small businesses, and contractors with strategies to minimize tax liability.',
      icon: <Calculator className="h-6 w-6 text-stone-200" />,
      link: '/services#tax-preparation',
    },
    {
      title: 'Bookkeeping',
      description:
        'Comprehensive bookkeeping services to keep your financial records organized, accurate, and up-to-date.',
      icon: <Book className="h-6 w-6 text-stone-200" />,
      link: '/services#bookkeeping',
    },
    {
      title: 'Business Consulting',
      description:
        'Strategic financial guidance to help your business grow, improve profitability, and achieve long-term success.',
      icon: <Briefcase className="h-6 w-6 text-stone-200" />,
      link: '/services#business-consulting',
    },
    {
      title: 'Payroll Services',
      description:
        'Comprehensive payroll management to ensure accurate, timely processing and compliance.',
      icon: <DollarSign className="h-6 w-6 text-stone-200" />,
      link: '/services#payroll-services',
    },
  ];

  // --- Schema Definitions ---

  // Schema for Contact Form - Updated with CAPTCHA
  const contactFormSchema = z.object({
    name: z.string().min(2, { message: 'Name must be at least 2 characters' }),
    email: z.string().email({ message: 'Please enter a valid email address' }),
    subject: z
      .string()
      .min(3, { message: 'Subject must be at least 3 characters' }),
    message: z
      .string()
      .min(10, { message: 'Message must be at least 10 characters' }),
    captcha: z
      .string()
      .min(1, { message: 'Please complete the CAPTCHA verification' }),
  });
  type ContactFormInputs = z.infer<typeof contactFormSchema>;

  // Schema for Appointment Request Form
  const appointmentRequestSchema = z.object({
    apptName: z
      .string()
      .min(2, { message: 'Name must be at least 2 characters' }),
    apptEmail: z
      .string()
      .email({ message: 'Please enter a valid email address' }),
    apptPhone: z.string().optional(), // Optional phone number
    preferredDate: z.string().min(1, { message: 'Please select a date' }),
    preferredTime: z.string().min(1, { message: 'Please select a time' }),
    apptMessage: z.string().optional(), // Optional message
  });
  type AppointmentRequestInputs = z.infer<typeof appointmentRequestSchema>;

  // Available time slots (displayed as buttons)
  const TIME_SLOTS = [
    '09:00',
    '10:00',
    '11:00',
    '12:00',
    '13:00',
    '14:00',
    '15:00',
    '16:00',
  ];

  // State for Contact Form
  const [isContactSubmitting, setIsContactSubmitting] = useState(false);
  const [contactSubmitStatus, setContactSubmitStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [contactCaptcha, setContactCaptcha] = useState<string>('');

  // State for Appointment Request Form - Updated
  const [isApptSubmitting, setIsApptSubmitting] = useState(false);
  const [apptSubmitStatus, setApptSubmitStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  // Latest-wins guard for availability fetches: a slow response for a
  // previously picked date must not overwrite the current date's slots
  const slotsRequestIdRef = useRef(0);
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Accordion stepper state for the Consultation Request form
  // 1 = Personal Info, 2 = Time & Date, 3 = Message
  const [formStep, setFormStep] = useState<FormStep>(1);
  const [maxStepReached, setMaxStepReached] = useState<FormStep>(1);

  // react-hook-form setup for Contact Form
  const contactForm = useForm<ContactFormInputs>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: '',
      email: '',
      subject: '',
      message: '',
      captcha: '',
    },
  });

  // react-hook-form setup for Appointment Request Form - Updated
  const apptForm = useForm<AppointmentRequestInputs>({
    resolver: zodResolver(appointmentRequestSchema),
    defaultValues: {
      apptName: '',
      apptEmail: '',
      apptPhone: '',
      preferredDate: '',
      preferredTime: '',
      apptMessage: '',
    },
  });

  // Move to a previously visited step (headers of unreached steps are disabled)
  const goToStep = (step: FormStep) => {
    if (step <= maxStepReached) setFormStep(step);
  };

  // Validate the current step's fields before advancing to the next one
  const handleStepContinue = async (from: 1 | 2) => {
    const fields =
      from === 1
        ? (['apptName', 'apptPhone', 'apptEmail'] as const)
        : (['preferredDate', 'preferredTime'] as const);
    const valid = await apptForm.trigger(fields);
    if (valid) {
      const next = (from + 1) as FormStep;
      setFormStep(next);
      setMaxStepReached(prev => (next > prev ? next : prev));
    }
  };

  // Fetch available time slots when date changes
  const fetchAvailableSlots = async (date: Date) => {
    const requestId = ++slotsRequestIdRef.current;
    setLoadingSlots(true);
    try {
      const dateString = formatLocalDate(date);

      const response = await fetch(
        `${API_BASE_URL}/api/appointments/availability?date=${dateString}`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (requestId !== slotsRequestIdRef.current) return;

      setAvailableSlots(data.availableSlots || []);
    } catch (error) {
      if (requestId !== slotsRequestIdRef.current) return;
      console.error('Error fetching available slots:', error);
      setAvailableSlots([]);

      // Show user-friendly error message
      setApptSubmitStatus({
        type: 'error',
        message:
          'Unable to load available time slots. Please try selecting a different date or refresh the page.',
      });
    } finally {
      if (requestId === slotsRequestIdRef.current) setLoadingSlots(false);
    }
  };

  // Handle date selection
  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    apptForm.setValue('preferredTime', ''); // Reset time when date changes

    if (date) {
      apptForm.setValue('preferredDate', formatLocalDate(date));
      fetchAvailableSlots(date);
    } else {
      // Invalidate any in-flight availability request
      slotsRequestIdRef.current++;
      setLoadingSlots(false);
      apptForm.setValue('preferredDate', '');
      setAvailableSlots([]);
    }
  };

  // Handle CAPTCHA changes
  const handleContactCaptchaChange = (token: string | null) => {
    setContactCaptcha(token || '');
    contactForm.setValue('captcha', token || '');
  };

  // Handle Contact Form submission - Updated with CAPTCHA validation
  const onContactSubmit: SubmitHandler<ContactFormInputs> = async data => {
    setIsContactSubmitting(true);
    setContactSubmitStatus(null);

    try {
      // Send data to the API route
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data), // Send validated data including CAPTCHA
      });

      const result = await response.json(); // Parse the response

      if (!response.ok) {
        // Handle API errors (e.g., validation errors from server, server errors)
        throw new Error(
          result.message || `HTTP error! status: ${response.status}`
        );
      }

      // Handle success
      setContactSubmitStatus({
        type: 'success',
        message: result.message || 'Your message has been sent successfully!',
      });
      contactForm.reset(); // Clear the form on success
      setContactCaptcha(''); // Reset CAPTCHA state
    } catch (error: any) {
      console.error('Contact form submission error:', error);
      setContactSubmitStatus({
        type: 'error',
        message:
          error.message || 'Failed to send message. Please try again later.',
      });
    } finally {
      setIsContactSubmitting(false);
    }
  };

  // Handle Appointment Request Form submission - Updated with CAPTCHA validation
  const onApptSubmit: SubmitHandler<AppointmentRequestInputs> = async data => {
    setIsApptSubmitting(true);
    setApptSubmitStatus(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/appointments/book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: data.apptName,
          email: data.apptEmail,
          phone: data.apptPhone,
          service: 'Consultation',
          preferredDate: data.preferredDate,
          preferredTime: data.preferredTime,
          message: data.apptMessage,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to book appointment');
      }

      setApptSubmitStatus({
        type: 'success',
        message:
          result.message || 'Your appointment has been booked successfully!',
      });
      apptForm.reset();
      setSelectedDate(undefined);
      setAvailableSlots([]);
      setFormStep(1);
      setMaxStepReached(1);
    } catch (error: any) {
      console.error('Appointment booking error:', error);
      setApptSubmitStatus({
        type: 'error',
        message:
          error.message ||
          'Failed to book appointment. Please try again or call us.',
      });
    } finally {
      setIsApptSubmitting(false);
    }
  };

  // If submit-time validation finds errors in an earlier step, jump back to it
  const onApptInvalid: SubmitErrorHandler<
    AppointmentRequestInputs
  > = errors => {
    if (errors.apptName || errors.apptPhone || errors.apptEmail) {
      setFormStep(1);
    } else if (errors.preferredDate || errors.preferredTime) {
      setFormStep(2);
    }
  };

  // Collapsed-header summaries for completed steps
  const [watchedName, watchedEmail] = apptForm.watch(['apptName', 'apptEmail']);
  const watchedTime = apptForm.watch('preferredTime');
  const personalSummary = [watchedName, watchedEmail]
    .filter(Boolean)
    .join(' · ');
  const timeDateSummary =
    selectedDate && watchedTime
      ? `${selectedDate.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        })} · ${formatTimeSlot(watchedTime)}`
      : '';

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />

      <div>
        <div className="bg-white">
          {/* Hero Section */}
          <HeroSection />

          {/* Services Section */}
          <section
            id="services"
            className="section text-stone-200 scroll-mt-24"
          >
            <div className="container">
              <div className="text-center max-w-3xl mx-auto mb-8">
                <h2 className="text-3xl lg:text-4xl font-bold text-stone-800 mb-2">
                  Our Services, Your Business Solution
                </h2>
                <p className="text-stone-600 text-lg">
                  From innovative software solutions to tailored consulting,
                  TIFPA offers a comprehensive suite of services designed to
                  streamline your operations, boost efficiency, and drive growth
                  for your business.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {services.map(service => (
                  <ServiceCard
                    key={service.title}
                    title={service.title}
                    description={service.description}
                    icon={service.icon}
                    link={service.link}
                  />
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 m-8">
              {testimonials.map(testimonial => (
                <TestimonialCard
                  key={testimonial.name}
                  name={testimonial.name}
                  business={testimonial.business}
                  testimonial={testimonial.testimonial}
                  rating={testimonial.rating}
                />
              ))}
            </div>
          </section>

          {/* CTA Section */}
          {/* <section className=" py-16 md:py-16 bg-tifpa-natural-leather">
            <div className="container z-10">
              <div className="text-center max-w-3xl mx-auto">
                <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-stone-800">
                  Ready to <span className="text-stone-800">Transform</span>{' '}
                  Your Financial Future?
                </h2>
                <p className="text-lg mb-8 opacity-90 text-stone-700">
                  Schedule a consultation today and discover how our tailored
                  financial services can help your business thrive.
                </p>
                <Button className="btn-primary">Book Your Consultation</Button>
              </div>
            </div>
          </section> */}

          {/* Testimonials Section */}
          {/* <section className="section "></section> */}
          {/* Contact Section */}
          <section
            id="consultation"
            className="section bg-tifpa-calendar scroll-mt-24"
          >
            <div className="container">
              <div className="text-center max-w-2xl mx-auto">
                <h2 className="text-3xl lg:text-4xl font-bold text-stone-800 mb-4">
                  Ready to <span className="text-tifpa-rose">Transform</span>{' '}
                  Your Financial Future?
                </h2>
                <p className="text-lg mb-8 opacity-90 text-stone-700">
                  Schedule a consultation today and discover how our tailored
                  financial services can help your business thrive.
                </p>
              </div>

              <div className="relative section grid grid-cols-1 xl:grid-cols-2 ">
                {/* Appointment Request Form */}

                <Card className="bg-white/70 border border-white shadow-xl shadow-modern-warm-gray/15 rounded-2xl transition-all duration-300 hover:shadow-2xl hover:shadow-modern-warm-gray/15">
                  <CardHeader>
                    <CardTitle className="text-modern-charcoal font-serif text-xl">
                      Consultation Request
                    </CardTitle>
                    <CardDescription className="text-modern-cool-gray">
                      Fill out this form to request a consultation. We'll
                      contact you to confirm.
                    </CardDescription>
                  </CardHeader>

                  <CardContent>
                    {/* Use Form component from shadcn */}
                    <Form {...apptForm}>
                      <form
                        onSubmit={apptForm.handleSubmit(
                          onApptSubmit,
                          onApptInvalid
                        )}
                        className="space-y-4"
                      >
                        {/* Step 1: Personal Info */}
                        <AccordionSection
                          step={1}
                          title="Personal Info"
                          summary={personalSummary}
                          isOpen={formStep === 1}
                          isComplete={maxStepReached > 1}
                          isReachable
                          onHeaderClick={() => goToStep(1)}
                        >
                          {/* Name and Phone in same row */}
                          <div className="grid grid-cols-5 gap-4">
                            <div className="col-span-3">
                              <FormField
                                control={apptForm.control}
                                name="apptName"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Name</FormLabel>
                                    <FormControl>
                                      <Input
                                        placeholder="Your Full Name"
                                        {...field}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                            <div className="col-span-2">
                              <FormField
                                control={apptForm.control}
                                name="apptPhone"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Phone (Optional)</FormLabel>
                                    <FormControl>
                                      <Input
                                        type="tel"
                                        placeholder="(123) 456-7890"
                                        {...field}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                          </div>

                          <FormField
                            control={apptForm.control}
                            name="apptEmail"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Email</FormLabel>
                                <FormControl>
                                  <Input
                                    type="email"
                                    placeholder="your.email@example.com"
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <Button
                            type="button"
                            className="modern-accent-button w-full"
                            onClick={() => handleStepContinue(1)}
                          >
                            Continue
                          </Button>
                        </AccordionSection>

                        {/* Step 2: Time & Date */}
                        <AccordionSection
                          step={2}
                          title="Time & Date"
                          summary={timeDateSummary}
                          isOpen={formStep === 2}
                          isComplete={maxStepReached > 2}
                          isReachable={maxStepReached >= 2}
                          onHeaderClick={() => goToStep(2)}
                        >
                          <FormField
                            control={apptForm.control}
                            name="preferredDate"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Preferred Date</FormLabel>
                                <FormControl>
                                  <div
                                    className="w-full"
                                    onClick={e => e.stopPropagation()}
                                  >
                                    <CustomCalendar
                                      mode="single"
                                      selected={selectedDate}
                                      onSelect={handleDateSelect}
                                      className="w-full rounded-md border shadow-sm mx-auto"
                                      disabled={date => {
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0);
                                        return date < today;
                                      }}
                                    />
                                  </div>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          {/* Time Slots - Button grid */}
                          <FormField
                            control={apptForm.control}
                            name="preferredTime"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Preferred Time</FormLabel>
                                <FormControl>
                                  <div className="space-y-3">
                                    {loadingSlots ? (
                                      <div className="text-center py-4">
                                        <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                                        <p className="text-sm text-muted-foreground mt-2">
                                          Loading available times...
                                        </p>
                                      </div>
                                    ) : !selectedDate ? (
                                      <p className="text-sm text-muted-foreground text-center py-4">
                                        Please select a date first
                                      </p>
                                    ) : availableSlots.length === 0 ? (
                                      <p className="text-sm text-muted-foreground text-center py-4">
                                        No available times on this date. Please
                                        select another day.
                                      </p>
                                    ) : (
                                      <div className="grid grid-cols-4 gap-2">
                                        {TIME_SLOTS.map(timeSlot => {
                                          const displayTime =
                                            formatTimeSlot(timeSlot);

                                          // Check if this time slot is available (API returns 24-hour format like "09:00", "13:00")
                                          const isAvailable =
                                            availableSlots.includes(timeSlot);
                                          const isSelected =
                                            field.value === timeSlot;

                                          return (
                                            <Button
                                              key={timeSlot}
                                              type="button"
                                              variant={
                                                isSelected
                                                  ? 'default'
                                                  : 'outline'
                                              }
                                              disabled={!isAvailable}
                                              className={cn(
                                                'h-12 text-sm transition-all duration-200',
                                                isSelected &&
                                                  'bg-tifpa-rose hover:bg-tifpa-rose hover:brightness-95 text-white',
                                                !isAvailable &&
                                                  'opacity-40 cursor-not-allowed'
                                              )}
                                              onClick={e => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (isAvailable) {
                                                  field.onChange(
                                                    isSelected ? '' : timeSlot
                                                  );
                                                }
                                              }}
                                            >
                                              {displayTime}
                                            </Button>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <Button
                            type="button"
                            className="modern-accent-button w-full"
                            onClick={() => handleStepContinue(2)}
                          >
                            Continue
                          </Button>
                        </AccordionSection>

                        {/* Step 3: Message */}
                        <AccordionSection
                          step={3}
                          title="Message"
                          isOpen={formStep === 3}
                          isComplete={false}
                          isReachable={maxStepReached >= 3}
                          onHeaderClick={() => goToStep(3)}
                        >
                          <FormField
                            control={apptForm.control}
                            name="apptMessage"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Message (Optional)</FormLabel>
                                <FormControl>
                                  <Textarea
                                    placeholder="Write us a message."
                                    rows={3}
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {/* Submit Button */}
                          <Button
                            className="modern-accent-button w-full py-3"
                            type="submit"
                            disabled={isApptSubmitting}
                          >
                            {isApptSubmitting && (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            {isApptSubmitting
                              ? 'Submitting...'
                              : 'Submit Appointment Request'}
                          </Button>
                        </AccordionSection>

                        {/* Submission Status Alert */}
                        {apptSubmitStatus && (
                          <Alert
                            variant={
                              apptSubmitStatus.type === 'success'
                                ? 'default'
                                : 'destructive'
                            }
                          >
                            {apptSubmitStatus.type === 'success' ? (
                              <CheckCircle className="h-4 w-4" />
                            ) : (
                              <AlertCircle className="h-4 w-4" />
                            )}
                            <AlertTitle>
                              {apptSubmitStatus.type === 'success'
                                ? 'Request Submitted!'
                                : 'Error'}
                            </AlertTitle>
                            <AlertDescription>
                              {apptSubmitStatus.message}
                            </AlertDescription>
                          </Alert>
                        )}
                      </form>
                    </Form>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
}
